import { useState } from 'react'
import { Row, Col, Card, List, Button, Input, Space, Popconfirm, Typography, Empty, Spin, message, Tooltip } from 'antd'
import { PlusOutlined, DeleteOutlined, SyncOutlined, DatabaseOutlined } from '@ant-design/icons'
import {
  useMarcheCustom,
  useCreateMarcaCustom,
  useDeleteMarcaCustom,
  useCreateModelloCustom,
  useDeleteModelloCustom,
  useSeedMarcheDefault,
  useSyncCarQuery,
  type MarcaCustom,
} from '@/hooks/useAuto'

const { Text } = Typography

export default function MarcheModelliSettings() {
  const { data: marche = [], isLoading } = useMarcheCustom()
  const createMarca = useCreateMarcaCustom()
  const deleteMarca = useDeleteMarcaCustom()
  const createModello = useCreateModelloCustom()
  const deleteModello = useDeleteModelloCustom()
  const seedMarche = useSeedMarcheDefault()
  const syncCarQuery = useSyncCarQuery()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [newMarca, setNewMarca] = useState('')
  const [newModello, setNewModello] = useState('')

  const currentMarca: MarcaCustom | null = marche.find(m => m.id === selectedId) ?? null

  const handleAddMarca = async () => {
    const nome = newMarca.trim()
    if (!nome) return
    try {
      const created = await createMarca.mutateAsync(nome)
      setNewMarca('')
      setSelectedId(created.id)
    } catch (err: any) {
      message.error(err?.response?.data?.detail ?? 'Errore nella creazione della marca')
    }
  }

  const handleDeleteMarca = async (id: number) => {
    if (selectedId === id) setSelectedId(null)
    try {
      await deleteMarca.mutateAsync(id)
    } catch {
      message.error('Errore nell\'eliminazione della marca')
    }
  }

  const handleAddModello = async () => {
    if (!currentMarca) return
    const nome = newModello.trim()
    if (!nome) return
    try {
      await createModello.mutateAsync({ marca_id: currentMarca.id, nome })
      setNewModello('')
    } catch (err: any) {
      message.error(err?.response?.data?.detail ?? 'Errore nella creazione del modello')
    }
  }

  const handleDeleteModello = async (id: number) => {
    try {
      await deleteModello.mutateAsync(id)
    } catch {
      message.error('Errore nell\'eliminazione del modello')
    }
  }

  const handleSeedDefault = async () => {
    const res = await seedMarche.mutateAsync().catch(() => null)
    if (res) message.success(`Aggiunte ${res.aggiunte} marche predefinite (totale: ${res.totale})`)
  }

  const handleSyncCarQuery = async () => {
    const res = await syncCarQuery.mutateAsync().catch(() => null)
    if (res) message.success(`Sincronizzate ${res.aggiunte} nuove marche da CarQuery (totale: ${res.totale})`)
  }

  if (isLoading) return <Spin style={{ display: 'block', margin: '32px auto' }} />

  return (
    <>
    <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
      <Tooltip title="Inserisce ~31 marche italiane comuni (FIAT, BMW, VW…) — salta i duplicati">
        <Button
          icon={<DatabaseOutlined />}
          loading={seedMarche.isPending}
          onClick={handleSeedDefault}
          size="small"
        >
          Carica marche predefinite
        </Button>
      </Tooltip>
      <Tooltip title="Importa tutte le marche da CarQuery API nel DB locale (operazione lenta, eseguire una volta sola)">
        <Button
          icon={<SyncOutlined />}
          loading={syncCarQuery.isPending}
          onClick={handleSyncCarQuery}
          size="small"
        >
          Sincronizza da CarQuery
        </Button>
      </Tooltip>
    </div>
    <Row gutter={16}>
      {/* Marche */}
      <Col xs={24} sm={10}>
        <Card
          title="Marche"
          size="small"
          extra={<Text type="secondary" style={{ fontSize: 12 }}>{marche.length} voci</Text>}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Nuova marca..."
                value={newMarca}
                size="small"
                onChange={e => setNewMarca(e.target.value)}
                onPressEnter={handleAddMarca}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                loading={createMarca.isPending}
                onClick={handleAddMarca}
              />
            </Space.Compact>
          </div>
          {marche.length === 0 ? (
            <Empty description="Nessuna marca personalizzata" style={{ padding: 16 }} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              size="small"
              dataSource={marche}
              style={{ maxHeight: 420, overflowY: 'auto' }}
              renderItem={marca => (
                <List.Item
                  style={{ padding: 0 }}
                  actions={[
                    <Popconfirm
                      key="del"
                      title={`Eliminare "${marca.nome}" e tutti i suoi modelli?`}
                      onConfirm={() => handleDeleteMarca(marca.id)}
                      okText="Elimina"
                      cancelText="Annulla"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                      />
                    </Popconfirm>,
                  ]}
                >
                  <div
                    style={{
                      flex: 1,
                      cursor: 'pointer',
                      padding: '5px 12px',
                      background: currentMarca?.id === marca.id ? '#e6f4ff' : undefined,
                      minHeight: 32,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                    onClick={() => setSelectedId(marca.id)}
                  >
                    <span style={{ fontWeight: currentMarca?.id === marca.id ? 600 : 400 }}>
                      {marca.nome}
                    </span>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      ({marca.modelli.length})
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Card>
      </Col>

      {/* Modelli */}
      <Col xs={24} sm={14}>
        <Card
          title={currentMarca ? `Modelli — ${currentMarca.nome}` : 'Modelli'}
          size="small"
          extra={currentMarca && (
            <Text type="secondary" style={{ fontSize: 12 }}>{currentMarca.modelli.length} voci</Text>
          )}
          styles={{ body: { padding: 0 } }}
        >
          {!currentMarca ? (
            <Empty
              description="Seleziona una marca per gestire i modelli"
              style={{ padding: 24 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder={`Nuovo modello per ${currentMarca.nome}...`}
                    value={newModello}
                    size="small"
                    onChange={e => setNewModello(e.target.value)}
                    onPressEnter={handleAddModello}
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="small"
                    loading={createModello.isPending}
                    onClick={handleAddModello}
                  />
                </Space.Compact>
              </div>
              {currentMarca.modelli.length === 0 ? (
                <Empty description="Nessun modello" style={{ padding: 16 }} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <List
                  size="small"
                  dataSource={currentMarca.modelli}
                  style={{ maxHeight: 420, overflowY: 'auto' }}
                  renderItem={mod => (
                    <List.Item
                      style={{ paddingLeft: 12, paddingRight: 8 }}
                      actions={[
                        <Popconfirm
                          key="del"
                          title={`Eliminare "${mod.nome}"?`}
                          onConfirm={() => handleDeleteModello(mod.id)}
                          okText="Elimina"
                          cancelText="Annulla"
                          okButtonProps={{ danger: true }}
                        >
                          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                        </Popconfirm>,
                      ]}
                    >
                      {mod.nome}
                    </List.Item>
                  )}
                />
              )}
            </>
          )}
        </Card>
      </Col>
    </Row>
    </>
  )
}
